import { Activity, BadgeDollarSign, CheckCircle2, ClipboardCheck, FileCheck2, Files, Mail, Pencil, Plus, Trash2, UsersRound } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { NextStepCard } from "../components/NextStepCard";
import { StatCard } from "../components/StatCard";
import { Shell } from "../components/Shell";
import { StatusPill } from "../components/StatusPill";
import { clients as clientsApi, dashboard, disputes as disputesApi, request, type ClientListItem, type DashboardStats, type DisputeRound } from "../lib/api";
import { SetupChecklist } from "../components/SetupChecklist";
import { clientInitials } from "../lib/clientName";

// Animates an integer from 0 to `value` over 400ms with ease-out-quint.
// Respects prefers-reduced-motion (snaps to final value, no animation).
function CountUp({ value }: { value: number }) {
  const [n, setN] = React.useState(0);
  React.useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { setN(value); return; }
    const start = performance.now();
    const duration = 400;
    let raf: number;
    const step = (t: number) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 5); // quint out
      setN(Math.round(value * eased));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return <>{n.toLocaleString()}</>;
}

export default function DashboardPage() {
  const nav = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [clientList, setClientList] = useState<ClientListItem[]>([]);
  const [rounds, setRounds] = useState<DisputeRound[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [seeding, setSeeding] = useState(false);

  async function loadAll() {
    try { setStats(await dashboard.stats()); } catch {}
    try {
      // Show everything in flight (onboarding + active + needs_review). Filter UI lives on /clients.
      const res = await clientsApi.list({ limit: 25 });
      const inFlight = (res.items || []).filter(
        c => c.status === "onboarding" || c.status === "active" || c.status === "needs_review"
      );
      setClientList(inFlight);
      if (inFlight[0]) setSelectedId(inFlight[0].id);
    } catch {}
    try { setRounds(await disputesApi.list({})); } catch {}
  }
  useEffect(() => { void loadAll(); }, []);

  // One click: drop a fully-wired DEMO client (report + analysis + signed
  // agreement) into this company so a new operator can explore the pipeline
  // before touching real data. Idempotent server-side.
  async function loadDemoClient() {
    setSeeding(true);
    try {
      const r = await request<{ ok: true; client_id: string }>("/api/demo/seed", { method: "POST" });
      await loadAll();
      nav(`/clients/${r.client_id}`);
    } catch (e: any) {
      alert(e?.message || "Could not load the demo client.");
    } finally {
      setSeeding(false);
    }
  }

  const selected = clientList.find((c) => c.id === selectedId) || clientList[0];

  function fmt(cents: number) {
    return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  return (
    <Shell title="No Excuses Dispute Manager" subtitle="Credit repair CRM">
      <SetupChecklist clientCount={clientList.length} onLoadDemo={seeding ? undefined : loadDemoClient} />
      {clientList.length === 0 && (
        <NextStepCard
          label="Your first client, start to finish"
          steps={[
            {
              label: "Add a client to begin",
              state: "next",
              actionLabel: seeding ? "Loading demo…" : "+ New client",
              onAction: () => nav("/clients/new"),
            },
            { label: "Send intake link", state: "todo" },
            { label: "Import credit report", state: "todo" },
            { label: "Generate dispute letters", state: "todo" },
            { label: "Mail letters", state: "todo" },
          ]}
        />
      )}

      <section className="stats-grid">
        <StatCard
          label="Active clients"
          value={stats ? <CountUp value={stats.active_clients} /> : <span className="ne-skeleton">00</span>}
          detail={`${stats?.onboarding ?? 0} in onboarding`}
          icon={<UsersRound size={20} aria-hidden="true" />}
        />
        <StatCard
          label="Letters ready"
          value={stats ? <CountUp value={stats.letters_ready} /> : <span className="ne-skeleton">00</span>}
          detail={`${stats?.letters_mailable_today ?? 0} can be mailed today`}
          icon={<Files size={20} aria-hidden="true" />}
        />
        <StatCard
          label="Disputes pending"
          value={stats ? <CountUp value={stats.disputes_pending} /> : <span className="ne-skeleton">00</span>}
          detail={`${stats?.responses_due_this_week ?? 0} responses due this week`}
          icon={<Activity size={20} aria-hidden="true" />}
        />
        <StatCard
          label="Monthly billing"
          value={stats ? fmt(stats.monthly_billing_cents) : <span className="ne-skeleton">$0.00</span>}
          detail={`${stats?.failed_payments ?? 0} failed payments to review`}
          icon={<BadgeDollarSign size={20} aria-hidden="true" />}
        />
      </section>

      <section className="main-grid">
        <div className="panel client-list-panel">
          <div className="panel-header">
            <div>
              <h2>Clients</h2>
            </div>
            <button className="ghost-button" onClick={() => nav("/clients/new")}>
              <Plus size={16} aria-hidden="true" /> Add
            </button>
          </div>
          <div className="client-list">
            {clientList.length === 0 && <p className="muted small">No active clients yet.</p>}
            {clientList.map((c) => (
              <button
                key={c.id}
                className={`client-row ${selected?.id === c.id ? "selected" : ""}`}
                onClick={() => setSelectedId(c.id)}
              >
                <div className="avatar" title={c.legal_name ?? undefined}>{clientInitials(c.legal_name)}</div>
                <div className="client-main">
                  <strong>{c.legal_name}</strong>
                  <span>{c.id.slice(0, 8)} · {c.assigned_agent || "Unassigned"}</span>
                </div>
                <StatusPill status={c.status} />
              </button>
            ))}
          </div>
        </div>

        {selected && (
          <div className="panel profile-panel">
            <div className="profile-header">
              <div>
                <h2>{selected.legal_name ?? "(unnamed)"}</h2>
                <span>{selected.phone || "—"} · {selected.email}</span>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <StatusPill status={selected.status} />
                <button
                  className="ghost-button"
                  onClick={() => nav(`/clients/${selected.id}`)}
                  title="Edit client"
                  aria-label="Edit client"
                >
                  <Pencil size={14} aria-hidden="true" /> Edit
                </button>
                <button
                  className="ghost-button"
                  onClick={async () => {
                    if (!confirm(`Delete ${selected.legal_name ?? "this client"}? This soft-deletes (status → churned) and can be restored.`)) return;
                    try {
                      await clientsApi.remove(selected.id);
                      setClientList(prev => prev.filter(c => c.id !== selected.id));
                      setSelectedId(null);
                    } catch (e: any) {
                      alert(`Delete failed: ${e?.message || "unknown error"}`);
                    }
                  }}
                  title="Delete client"
                  aria-label="Delete client"
                  style={{ color: "var(--ne-red)" }}
                >
                  <Trash2 size={14} aria-hidden="true" /> Delete
                </button>
              </div>
            </div>

            <div className="profile-tabs" role="tablist" aria-label="Client profile sections">
              <button
                className="selected"
                role="tab"
                aria-selected={true}
                aria-controls="client-overview-panel"
                id="client-overview-tab"
              >
                Overview
              </button>
              <button role="tab" aria-selected={false} onClick={() => nav(`/clients/${selected.id}`)}>Items</button>
              <button role="tab" aria-selected={false} onClick={() => nav(`/clients/${selected.id}`)}>Disputes</button>
              <button role="tab" aria-selected={false} onClick={() => nav(`/clients/${selected.id}`)}>Documents</button>
              <button role="tab" aria-selected={false} onClick={() => nav(`/clients/${selected.id}`)}>Messages</button>
            </div>

            <section
              id="client-overview-panel"
              role="tabpanel"
              aria-labelledby="client-overview-tab"
            >
              <h3 className="sr-only">Client metrics</h3>
              <div className="profile-metrics">
                <div><p>Start score</p><strong>{selected.score_start ?? "—"}</strong></div>
                <div><p>Current score</p><strong>{selected.score_current ?? "—"}</strong></div>
                {/* negative_items / deleted_items have no backend source yet —
                    nothing populates them. Show "—" rather than a fabricated 0,
                    which read as "this client has zero negative items". */}
                <div><p>Negative items</p><strong>{selected.negative_items ?? "—"}</strong></div>
                <div><p>Deleted</p><strong>{selected.deleted_items ?? "—"}</strong></div>
              </div>

              <div className="split-section">
                <section className="sub-panel">
                  <div className="sub-heading"><ClipboardCheck size={18} aria-hidden="true" /><h3>Open disputes</h3></div>
                  <p className="body-copy">
                    {rounds.filter(r => r.client_id === selected.id).length} round(s) in flight.
                  </p>
                  <button className="wide-button" onClick={() => nav("/disputes")}>View all</button>
                </section>
                <section className="sub-panel">
                  <div className="sub-heading"><FileCheck2 size={18} aria-hidden="true" /><h3>Compliance</h3></div>
                  <p className="body-copy">
                    Service agreement: {selected.service_agreement_signed ? "Signed" : "Not signed — work blocked"}.
                  </p>
                  <button className="wide-button" onClick={() => nav("/compliance")}>Open</button>
                </section>
              </div>
            </section>
          </div>
        )}

        <div className="panel action-panel">
          <div className="panel-header">
            <div>
              <h2>Next steps</h2>
              <span className="muted small">
                {selected ? `For ${selected.legal_name}` : "Pick a client to see their next actions"}
              </span>
            </div>
          </div>
          <div className="task-stack">
            <article>
              <CheckCircle2 size={18} aria-hidden="true" />
              <div>
                <strong>{selected?.next_action || "Review onboarding pipeline"}</strong>
                <span>Assigned to {selected?.assigned_agent || "team"}</span>
              </div>
            </article>
            <article>
              <FileCheck2 size={18} aria-hidden="true" />
              <div>
                <strong>Compliance documents</strong>
                <span>CROA disclosure and right to cancel tracked per client</span>
              </div>
            </article>
            <article>
              <Mail size={18} aria-hidden="true" />
              <div>
                <strong>Mail provider boundary</strong>
                <span>Provider-agnostic interface — Lob / LetterStream / other</span>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section className="lower-grid">
        <div className="panel">
          <div className="panel-header">
            <div><h2>Rounds</h2></div>
            <button className="ghost-button" onClick={() => nav("/disputes")}>
              <ClipboardCheck size={16} aria-hidden="true" /> Open all
            </button>
          </div>
          <div className="table-list">
            {rounds.slice(0, 6).map((r) => (
              <div className="table-row" key={r.id}>
                <span>{r.id.slice(0, 8)}</span>
                <strong>{r.client_name || r.client_id.slice(0, 8)}</strong>
                <span>Round {r.round_number}</span>
                <span>{r.bureau}</span>
                <span>{r.items_count} items</span>
                <StatusPill status={r.status} />
                <span>{r.due_date || "—"}</span>
              </div>
            ))}
            {rounds.length === 0 && <p className="muted small">No dispute rounds yet.</p>}
          </div>
        </div>
      </section>
    </Shell>
  );
}
